import React from "react";
import { BannerDto } from "../../../Common/api/models/AppConfiguration";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
} from "@mui/material";
import { Edit, Delete, Check, Close } from "@mui/icons-material";
import { mapCategoryToSeverity } from "../../../Common/utils/bannerMapping";
import { CATEGORIES, VARIANTS } from "../model/consts";
import dayjs from "dayjs";

interface AdminBannerConfigGridProps {
  banners: BannerDto[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const AdminBannerConfigGrid = ({
  banners,
  onEdit,
  onDelete,
}: AdminBannerConfigGridProps): JSX.Element => {
  const getCategoryLabel = (category: string) =>
    category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <Box pl="80px" pt="24px" data-testid="admin-banner-grid">
      <TableContainer
        component={Paper}
        sx={{ width: "92%", pb: 0, mb: "70px", mt: "10px" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "35%" }}>
                <b>Text</b>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <b>Category</b>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <b>Variant</b>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <b>Publish Date</b>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <b>Unpublish Date</b>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <b>Active</b>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners.map((b) => {
              const severity = CATEGORIES.find((c) => c.value === b.category);
              const variant = VARIANTS.find(
                (v) => v.toLowerCase() === b.variant
              );
              return (
                <TableRow key={b.id}>
                  <TableCell>{b.text}</TableCell>
                  <TableCell>
                    {severity && (
                      <Chip
                        label={getCategoryLabel(severity.value)}
                        color={
                          mapCategoryToSeverity(severity.value) as
                            | "default"
                            | "error"
                            | "info"
                            | "success"
                            | "warning"
                        }
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>{variant}</TableCell>
                  <TableCell>
                    {b.publishDate
                      ? dayjs(b.publishDate).format("DD-MM-YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {b.unpublishDate
                      ? dayjs(b.unpublishDate).format("DD-MM-YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {b.isActive ? (
                      <Tooltip title="Yes">
                        <IconButton size="small" color="primary">
                          <Check />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No">
                        <IconButton size="small" color="error">
                          <Close />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit Banner">
                      <IconButton
                        onClick={() => onEdit(b.id)}
                        size="small"
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Banner">
                      <IconButton
                        onClick={() => onDelete(b.id)}
                        size="small"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminBannerConfigGrid;
